import React, { useState } from 'react';
import { Form, Button, Alert, Row, Col } from 'react-bootstrap';

const DeviceEmailNotificationsPanel = ({ sensors, emailNotifications, setEmailNotifications }) => {
  const [newRuleSensor, setNewRuleSensor] = useState('');
  const [newRuleOperator, setNewRuleOperator] = useState('<');
  const [newRuleThreshold, setNewRuleThreshold] = useState('');

  const addAlertRule = () => {
    if (!newRuleSensor || !newRuleThreshold) return;

    const newId = emailNotifications.alertRules.length
      ? Math.max(...emailNotifications.alertRules.map((r) => r.id)) + 1
      : 1;

    setEmailNotifications((prev) => ({
      ...prev,
      alertRules: [...prev.alertRules, {
        id: newId,
        sensor: newRuleSensor,
        operator: newRuleOperator,
        threshold: parseFloat(newRuleThreshold),
        enabled: true,
      }],
    }));

    setNewRuleSensor('');
    setNewRuleOperator('<');
    setNewRuleThreshold('');
  };

  return (
    <div className="setup-panel">
      <h4 className="mb-3">Email Notifications</h4>
      <Row className="mb-3">
        <Col md={12}>
          <Form.Check
            type="switch"
            id="email-notifications-enabled"
            label="Enable Email Notifications"
            checked={emailNotifications.enabled}
            onChange={(e) => setEmailNotifications((prev) => ({
              ...prev,
              enabled: e.target.checked,
            }))}
          />
        </Col>
      </Row>

      {emailNotifications.enabled && (
        <div className="mt-4">
          <h5 className="mb-3">Alert Rules</h5>
          <p className="text-muted small mb-3">
            Create custom alert rules for any sensor. You&apos;ll receive emails when sensor readings meet the specified conditions.
          </p>

          <div className="setup-panel--nested">
            <h6 className="mb-3">Add Alert Rule</h6>
            <Row className="align-items-end">
              <Col md={4}>
                <Form.Label>Sensor</Form.Label>
                <Form.Select
                  value={newRuleSensor}
                  onChange={(e) => setNewRuleSensor(e.target.value)}
                  disabled={sensors.length === 0}
                >
                  <option value="">Select sensor</option>
                  {sensors.map((sensor, idx) => (
                    <option key={idx} value={sensor.sensorID}>
                      {sensor.name || sensor.sensorType}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={2}>
                <Form.Label>Condition</Form.Label>
                <Form.Select value={newRuleOperator} onChange={(e) => setNewRuleOperator(e.target.value)}>
                  <option value="<">{'<'}</option>
                  <option value=">">{'>'}</option>
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Label>Threshold Value</Form.Label>
                <Form.Control
                  type="number"
                  value={newRuleThreshold}
                  onChange={(e) => setNewRuleThreshold(e.target.value)}
                  placeholder="e.g., 30"
                />
              </Col>
              <Col md={3}>
                <Button
                  variant="outline-primary"
                  onClick={addAlertRule}
                  disabled={sensors.length === 0 || !newRuleSensor || !newRuleThreshold}
                >
                  + Add Rule
                </Button>
              </Col>
            </Row>
          </div>

          {emailNotifications.alertRules.length > 0 && (
            <div>
              <h6 className="mb-3">Active Alert Rules</h6>
              {emailNotifications.alertRules.map((rule, index) => (
                <div key={rule.id} className="setup-panel--nested">
                  <Row className="align-items-center mb-2">
                    <Col md={1}>
                      <Form.Check
                        type="switch"
                        checked={rule.enabled}
                        onChange={(e) => {
                          const updatedRules = [...emailNotifications.alertRules];
                          updatedRules[index].enabled = e.target.checked;
                          setEmailNotifications((prev) => ({ ...prev, alertRules: updatedRules }));
                        }}
                      />
                    </Col>
                    <Col md={3}>
                      <Form.Label className="small">Sensor</Form.Label>
                      <Form.Select
                        size="sm"
                        value={rule.sensor}
                        onChange={(e) => {
                          const updatedRules = [...emailNotifications.alertRules];
                          updatedRules[index].sensor = e.target.value;
                          setEmailNotifications((prev) => ({ ...prev, alertRules: updatedRules }));
                        }}
                      >
                        <option value="">Select sensor</option>
                        {sensors.map((sensor, idx) => (
                          <option key={idx} value={sensor.sensorID}>
                            {sensor.name || sensor.sensorType}
                          </option>
                        ))}
                      </Form.Select>
                    </Col>
                    <Col md={2}>
                      <Form.Label className="small">Condition</Form.Label>
                      <Form.Select
                        size="sm"
                        value={rule.operator}
                        onChange={(e) => {
                          const updatedRules = [...emailNotifications.alertRules];
                          updatedRules[index].operator = e.target.value;
                          setEmailNotifications((prev) => ({ ...prev, alertRules: updatedRules }));
                        }}
                      >
                        <option value="<">{'<'}</option>
                        <option value=">">{'>'}</option>
                      </Form.Select>
                    </Col>
                    <Col md={3}>
                      <Form.Label className="small">Threshold</Form.Label>
                      <Form.Control
                        size="sm"
                        type="number"
                        value={rule.threshold || ''}
                        onChange={(e) => {
                          const updatedRules = [...emailNotifications.alertRules];
                          updatedRules[index].threshold = e.target.value ? parseFloat(e.target.value) : null;
                          setEmailNotifications((prev) => ({ ...prev, alertRules: updatedRules }));
                        }}
                        placeholder="e.g., 30"
                      />
                    </Col>
                    <Col md={2}>
                      <Form.Label className="small">Auto Reset</Form.Label>
                      <Form.Check
                        type="switch"
                        checked={rule.autoReset !== undefined ? rule.autoReset : true}
                        onChange={(e) => {
                          const updatedRules = [...emailNotifications.alertRules];
                          updatedRules[index].autoReset = e.target.checked;
                          setEmailNotifications((prev) => ({ ...prev, alertRules: updatedRules }));
                        }}
                      />
                    </Col>
                    <Col md={1}>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => {
                          setEmailNotifications((prev) => ({
                            ...prev,
                            alertRules: prev.alertRules.filter((r) => r.id !== rule.id),
                          }));
                        }}
                      >
                        ×
                      </Button>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={12}>
                      <small className="text-muted">
                        Alert when
                        {' '}
                        {(() => {
                          const sensor = sensors.find((s) => s.sensorID === rule.sensor);
                          return sensor ? (sensor.name || sensor.sensorType) : 'sensor';
                        })()}
                        {' '}
                        {rule.operator}
                        {' '}
                        {rule.threshold || 'value'}
                        {rule.autoReset !== false && <span> • Auto-reset enabled</span>}
                        {rule.autoReset === false && <span> • Manual reset required</span>}
                      </small>
                    </Col>
                  </Row>
                </div>
              ))}
            </div>
          )}

          {emailNotifications.alertRules.length === 0 && (
            <Alert variant="secondary">
              <strong>No alert rules configured.</strong>
              {' '}
              Add rules above to start receiving email notifications when sensor readings meet your conditions.
            </Alert>
          )}

          <div className="mt-4 pt-4 border-top">
            <h5 className="mb-3">Device Summary</h5>
            <p className="text-muted small mb-3">
              Receive periodic summary emails showing all your sensor readings from the selected time period.
            </p>
            <Row className="align-items-center">
              <Col md={4}>
                <Form.Check
                  type="switch"
                  id="device-summary-enabled"
                  label="Enable Device Summary"
                  checked={emailNotifications.deviceSummary.enabled}
                  onChange={(e) => setEmailNotifications((prev) => ({
                    ...prev,
                    deviceSummary: { ...prev.deviceSummary, enabled: e.target.checked },
                  }))}
                />
              </Col>
              <Col md={4}>
                <Form.Label>Summary Frequency</Form.Label>
                <Form.Select
                  value={emailNotifications.deviceSummary.frequency}
                  onChange={(e) => setEmailNotifications((prev) => ({
                    ...prev,
                    deviceSummary: { ...prev.deviceSummary, frequency: e.target.value },
                  }))}
                  disabled={!emailNotifications.deviceSummary.enabled}
                >
                  <option value="12hours">Every 12 Hours</option>
                  <option value="1day">Daily</option>
                  <option value="3days">Every 3 Days</option>
                  <option value="5days">Every 5 Days</option>
                  <option value="1week">Weekly</option>
                </Form.Select>
              </Col>
              <Col md={4}>
                <small className="text-muted">
                  {emailNotifications.deviceSummary.enabled
                    ? `Summary emails will be sent ${
                      emailNotifications.deviceSummary.frequency === '12hours' ? 'twice daily'
                        : emailNotifications.deviceSummary.frequency === '1day' ? 'once per day'
                          : emailNotifications.deviceSummary.frequency === '3days' ? 'every 3 days'
                            : emailNotifications.deviceSummary.frequency === '5days' ? 'every 5 days'
                              : 'once per week'
                    }`
                    : 'Device summary emails are disabled'}
                </small>
              </Col>
            </Row>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeviceEmailNotificationsPanel;